import { jsxs, jsx, Fragment } from 'react/jsx-runtime';
import { Button } from '../ui/button.js';
import { Label } from '../ui/label.js';
import { Progress } from '../ui/progress.js';
import { useAuth, useOrganization } from '@clerk/clerk-react';
import { Upload, X, ImageIcon, AlertTriangle } from 'lucide-react';
import { useState, useRef, useEffect, useCallback } from 'react';
import { toast } from '../../lib/toast.js';

/** Well-known aspect ratios for display labels (checked with tolerance for floating-point safety) */
const KNOWN_RATIOS = [
    { ratio: 1, label: '1:1', examples: '1000×1000, 1200×1200' },
    { ratio: 4 / 3, label: '4:3', examples: '1200×900, 1600×1200' },
    { ratio: 3 / 2, label: '3:2', examples: '1200×800, 1800×1200' },
    { ratio: 16 / 9, label: '16:9', examples: '1920×1080, 1280×720' },
    { ratio: 21 / 9, label: '21:9', examples: '2560×1080' },
];
function formatAspectRatio(ratio) {
    const match = KNOWN_RATIOS.find(k => Math.abs(k.ratio - ratio) / k.ratio < 0.005);
    if (match)
        return { label: match.label, examples: match.examples };
    return { label: `${ratio.toFixed(2)}:1`, examples: '' };
}
/**
 * Beautiful drag-and-drop image upload component
 * Uploads to /api/v1/media endpoint and returns CDN URL
 */
function ImageUploadDropzone({ value, onChange, label = 'Featured Image', maxSizeMB = 5, required = false, error, targetAspectRatio, getPreviewUrl, getValueFromResponse, compact = false, }) {
    const { getToken } = useAuth();
    const { organization } = useOrganization();
    const [isDragging, setIsDragging] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [previewUrl, setPreviewUrl] = useState(value ? (getPreviewUrl ? getPreviewUrl(value) : value) : undefined);
    const [aspectWarning, setAspectWarning] = useState(null);
    const fileInputRef = useRef(null);
    const maxSizeBytes = maxSizeMB * 1024 * 1024;
    /**
     * Sync previewUrl with external value prop changes
     * This ensures the component displays existing images when editing
     */
    useEffect(() => {
        setPreviewUrl(value ? (getPreviewUrl ? getPreviewUrl(value) : value) : undefined);
    }, [value, getPreviewUrl]);
    /**
     * Validate file type and size
     */
    const validateFile = useCallback((file) => {
        if (!file.type.startsWith('image/')) {
            return 'Please upload an image file';
        }
        if (file.size > maxSizeBytes) {
            return `File size must be less than ${maxSizeMB}MB`;
        }
        return null;
    }, [maxSizeBytes, maxSizeMB]);
    /**
     * Upload file to server
     */
    const uploadFile = useCallback(async (file) => {
        try {
            setIsUploading(true);
            setUploadProgress(0);
            const formData = new FormData();
            formData.append('file', file);
            // Simulate progress (replace with actual progress if API supports it)
            const progressInterval = setInterval(() => {
                setUploadProgress(prev => Math.min(prev + 10, 90));
            }, 200);
            // Use fetch directly instead of apiClient to avoid JSON transformation
            const token = await getToken();
            const headers = {
                Authorization: `Bearer ${token}`,
            };
            const fetchResponse = await fetch('/api/v1/media', {
                method: 'POST',
                body: formData,
                headers,
            });
            if (!fetchResponse.ok) {
                const errorData = await fetchResponse.json();
                throw new Error(errorData.error || 'Upload failed');
            }
            const response = await fetchResponse.json();
            clearInterval(progressInterval);
            setUploadProgress(100);
            // Set the value from the response (URL by default, or custom extraction)
            const data = response.data || response;
            const resultValue = getValueFromResponse ? getValueFromResponse(data) : data.url;
            onChange(resultValue);
            setPreviewUrl(getPreviewUrl ? getPreviewUrl(resultValue) : data.url);
            toast.success('Image uploaded successfully');
        }
        catch (err) {
            const errorMessage = err?.message || 'Failed to upload image';
            toast.error(errorMessage);
        }
        finally {
            setIsUploading(false);
            setUploadProgress(0);
        }
    }, [getToken, onChange, organization?.id, getValueFromResponse, getPreviewUrl]);
    /**
     * Check image aspect ratio against target.
     * Returns 'error' if way off (>25%), 'warning' if slightly off (>10%), or null if fine.
     */
    const checkAspectRatio = useCallback((file) => {
        if (!targetAspectRatio)
            return Promise.resolve({ level: null, message: null });
        return new Promise(resolve => {
            const img = new Image();
            img.onload = () => {
                const ratio = img.width / img.height;
                const deviation = Math.abs(ratio - targetAspectRatio) / targetAspectRatio;
                const targetLabel = formatAspectRatio(targetAspectRatio).label;
                const actualLabel = `${img.width}×${img.height}`;
                if (deviation > 0.25) {
                    resolve({
                        level: 'error',
                        message: `Image aspect ratio is way off (${actualLabel}). Please use a ${targetLabel} image.`,
                    });
                }
                else if (deviation > 0.10) {
                    resolve({
                        level: 'warning',
                        message: `Image is not quite ${targetLabel} (${actualLabel}). It'll work but a ${targetLabel} image would look best.`,
                    });
                }
                else {
                    resolve({ level: null, message: null });
                }
                URL.revokeObjectURL(img.src);
            };
            img.onerror = () => {
                URL.revokeObjectURL(img.src);
                resolve({ level: null, message: null });
            };
            img.src = URL.createObjectURL(file);
        });
    }, [targetAspectRatio]);
    /**
     * Handle file selection
     */
    const handleFileSelect = useCallback(async (file) => {
        const validationError = validateFile(file);
        if (validationError) {
            toast.error(validationError);
            return;
        }
        // Check aspect ratio
        const aspect = await checkAspectRatio(file);
        if (aspect.level === 'error') {
            toast.error(aspect.message);
            setAspectWarning(null);
            return;
        }
        setAspectWarning(aspect.message);
        if (aspect.level === 'warning') {
            toast.warning(aspect.message);
        }
        uploadFile(file);
    }, [validateFile, checkAspectRatio, uploadFile]);
    /**
     * Handle drag events
     */
    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragging(true);
    };
    const handleDragLeave = (e) => {
        e.preventDefault();
        setIsDragging(false);
    };
    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        const file = e.dataTransfer.files[0];
        if (file) {
            handleFileSelect(file);
        }
    };
    /**
     * Handle file input change
     */
    const handleInputChange = (e) => {
        const file = e.target.files?.[0];
        if (file) {
            handleFileSelect(file);
        }
    };
    /**
     * Handle remove image
     */
    const handleRemove = () => {
        onChange(undefined);
        setPreviewUrl(undefined);
        setAspectWarning(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };
    /**
     * Open file picker
     */
    const handleClick = () => {
        fileInputRef.current?.click();
    };
    const ratioInfo = targetAspectRatio ? formatAspectRatio(targetAspectRatio) : null;
    // Compact tile — a 64px square preview/placeholder that opens the same file
    // picker on click. Reuses every handler above; only the chrome differs.
    if (compact) {
        return (jsxs("div", { className: "grid gap-1.5", children: [label && (jsxs(Label, { className: "text-xs", children: [label, required && jsx("span", { className: "text-destructive ml-1", children: "*" })] })), jsx("input", { ref: fileInputRef, type: "file", accept: "image/*", onChange: handleInputChange, className: "hidden" }), previewUrl && !isUploading ? (jsxs("div", { className: "group relative h-16 w-16 shrink-0", children: [jsx("div", { className: "flex h-16 w-16 items-center justify-center overflow-hidden rounded-lg border bg-muted", children: jsx("img", { src: previewUrl, alt: "Icon preview", className: "h-full w-full object-contain" }) }), jsxs("div", { className: "absolute inset-0 flex items-center justify-center gap-1 rounded-lg bg-black/60 opacity-0 transition-opacity group-hover:opacity-100", children: [jsx("button", { type: "button", onClick: handleClick, "aria-label": "Replace icon", className: "rounded p-1 text-white hover:bg-white/20", children: jsx(Upload, { className: "h-4 w-4" }) }), jsx("button", { type: "button", onClick: handleRemove, "aria-label": "Remove icon", className: "rounded p-1 text-white hover:bg-white/20", children: jsx(X, { className: "h-4 w-4" }) })] })] })) : (jsx("button", { type: "button", onClick: handleClick, onDragOver: handleDragOver, onDragLeave: handleDragLeave, onDrop: handleDrop, disabled: isUploading, "aria-label": label ? `Upload ${label}` : 'Upload icon', className: `
              flex h-16 w-16 shrink-0 items-center justify-center rounded-lg border-2 border-dashed transition-all
              ${isDragging ? 'border-primary bg-primary/5' : 'border-border bg-muted/50'}
              ${isUploading ? 'pointer-events-none' : 'hover:border-primary hover:bg-primary/5'}
            `, children: isUploading ? (jsx(Upload, { className: "h-5 w-5 animate-pulse text-muted-foreground" })) : (jsx(ImageIcon, { className: "h-5 w-5 text-muted-foreground" })) })), error && jsx("p", { className: "text-xs text-destructive", children: error })] }));
    }
    return (jsxs("div", { className: "grid gap-2", children: [label && (jsxs(Label, { children: [label, required && jsx("span", { className: "text-destructive ml-1", children: "*" })] })), jsx("input", { ref: fileInputRef, type: "file", accept: "image/*", onChange: handleInputChange, className: "hidden" }), previewUrl && !isUploading ? (jsx("div", { className: "relative group", children: jsxs("div", { className: "relative overflow-hidden rounded-xl border-2 border-border bg-muted h-48 flex items-center justify-center p-4", children: [jsx("img", { src: previewUrl, alt: "Preview", className: "h-full w-auto object-contain rounded-xl" }), jsxs("div", { className: "absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2", children: [jsxs(Button, { type: "button", variant: "secondary", size: "sm", onClick: handleClick, children: [jsx(Upload, { className: "h-4 w-4 mr-2" }), "Replace"] }), jsxs(Button, { type: "button", variant: "destructive", size: "sm", onClick: handleRemove, children: [jsx(X, { className: "h-4 w-4 mr-2" }), "Remove"] })] })] }) })) : (jsx("div", { onClick: handleClick, onDragOver: handleDragOver, onDragLeave: handleDragLeave, onDrop: handleDrop, className: `
            relative cursor-pointer rounded-xl border-2 border-dashed transition-all
            ${isDragging ? 'border-primary bg-primary/5' : 'border-border bg-muted/50'}
            ${isUploading ? 'pointer-events-none' : 'hover:border-primary hover:bg-primary/5'}
            h-48 flex flex-col items-center justify-center gap-3 p-6
          `, children: isUploading ? (jsxs(Fragment, { children: [jsx(Upload, { className: "h-12 w-12 text-muted-foreground animate-pulse" }), jsxs("div", { className: "w-full max-w-xs space-y-2", children: [jsx(Progress, { value: uploadProgress, className: "h-2" }), jsxs("p", { className: "text-sm text-muted-foreground text-center", children: ["Uploading... ", uploadProgress, "%"] })] })] })) : (jsxs(Fragment, { children: [jsx("div", { className: "rounded-full bg-primary/10 p-4", children: jsx(ImageIcon, { className: "h-8 w-8 text-primary" }) }), jsxs("div", { className: "text-center space-y-1", children: [jsxs("p", { className: "text-sm font-medium", children: [jsx("span", { className: "text-primary", children: "Click to upload" }), " or drag and drop"] }), jsxs("p", { className: "text-xs text-muted-foreground", children: ["PNG, JPG, GIF up to ", maxSizeMB, "MB"] }), ratioInfo && (jsxs("p", { className: "text-xs text-muted-foreground", children: [ratioInfo.label, " aspect ratio recommended", ratioInfo.examples ? ` (e.g. ${ratioInfo.examples})` : ''] }))] })] })) })), aspectWarning && (jsxs("div", { className: "flex items-start gap-2 rounded-lg border border-yellow-300 bg-yellow-50 p-3 text-sm text-yellow-800", children: [jsx(AlertTriangle, { className: "h-4 w-4 mt-0.5 shrink-0" }), jsx("span", { children: aspectWarning })] })), error && jsx("p", { className: "text-sm text-destructive", children: error })] }));
}

export { ImageUploadDropzone };
//# sourceMappingURL=ImageUploadDropzone.js.map
